import { inject, Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { LoginRequest } from './login-request';
import { LoginResponse } from './login-response';
import { User } from './user';
import { Role } from './roles';
import { CompanyContextService } from '../tenancy/company-context.service';
import { isProblemDetails } from '../http/api-error.model';
import { CompanyService } from '@features/company/data/company.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly companyService = inject(CompanyService);

  // Signals for state management
  private currentUserSignal = signal<User | null>(null);
  private isAuthenticatedSignal = signal<boolean>(false);
  private isLoadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private jwtTokenSignal = signal<string | null>(null);

  // Public readonly signals
  public currentUser = this.currentUserSignal.asReadonly();
  public isAuthenticated = this.isAuthenticatedSignal.asReadonly();
  public isLoading = this.isLoadingSignal.asReadonly();
  public error = this.errorSignal.asReadonly();

  // Computed signals for derived state
  public userName = computed(() => this.currentUserSignal()?.firstName || 'Guest');
  public userEmail = computed(() => this.currentUserSignal()?.email || '');

  public hasRole(role: Role): boolean {
    return this.currentUserSignal()?.roles?.includes(role) ?? false;
  }

  public readonly isSystemAdmin = computed(() => this.hasRole(Role.SystemAdmin));

  login(request: LoginRequest): Observable<void> {
    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    const payload: LoginRequest = {
      email: request.email.trim(),
      password: request.password,
    };

    return this.http
      .post<LoginResponse>(this.buildAuthUrl('/account/login'), payload, { withCredentials: true })
      .pipe(
        switchMap((loginResponse) => {
          this.handleAuthResponse(loginResponse);
          this.isLoadingSignal.set(false);

          return forkJoin({
            userInfo: this.http.get<User>(this.buildAuthUrl('/account/userInformation'), {
              withCredentials: true,
            }),
            // Add more parallel GET requests here if needed
          });
        }),
        tap((response) => {
          this.currentUserSignal.set(response.userInfo);
          this.isLoadingSignal.set(false);
        }),
        map(() => undefined),
        catchError((error) => {
          this.isLoadingSignal.set(false);
          const detail = isProblemDetails(error.error) ? error.error.detail : error.error?.message;
          this.errorSignal.set(detail || 'Login failed');
          this.clearAuthData();
          return throwError(() => error);
        }),
      );
  }

  logout(): void {
    this.clearAuthData();
  }

  checkAuthStatus(): Observable<void> {
    return this.http
      .get<User>(this.buildAuthUrl('/account/userInformation'), { withCredentials: true })
      .pipe(
        tap((user) => {
          this.currentUserSignal.set(user);
          this.isAuthenticatedSignal.set(true);
        }),
        map(() => undefined),
        catchError(() => {
          this.clearAuthData();
          return of(undefined);
        }),
      );
  }

  /**
   * Handle Refresh Token logic (if needed)
   */
  refreshToken(): Observable<void> {
    return this.http
      .post<LoginResponse>(this.buildAuthUrl('/account/refresh'), {}, { withCredentials: true })
      .pipe(
        tap((response) => {
          this.handleAuthResponse(response);
        }),
        map(() => undefined),
        catchError((error) => {
          this.errorSignal.set(error.error?.message || 'Token refresh failed');
          this.clearAuthData();
          return throwError(() => error);
        }),
      );
  }

  /**
   * Handle authentication response and update signals
   */
  private handleAuthResponse(response: LoginResponse): void {
    // Update signals
    this.isAuthenticatedSignal.set(true);
    this.errorSignal.set(null);

    // Optionally store in localStorage for persistence
    localStorage.setItem('isAuthenticated', 'true');
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    this.currentUserSignal.set(null);
    this.isAuthenticatedSignal.set(false);
    this.errorSignal.set(null);
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    this.companyContextService.clearSelection();
    this.companyService.invalidateCache();
  }

  private buildAuthUrl(path: string): string {
    const normalizedBasePath = environment.baseApiAuthPath.replace(/\/$/, '');
    const normalizedPath = path.replace(/^\//, '');
    return `${normalizedBasePath}/${normalizedPath}`;
  }
}
